import React, {
  createContext,
  useState,
  useEffect,
  useRef,
  ReactNode,
} from 'react';
import * as Sentry from '@sentry/react';
import { toast } from 'sonner';
import { GoogleCallbackResponseSchema } from '@daddytime/api-contracts';
import { API_BASE_URL } from '../config/api';
import { cognitoConfig, getCognitoHostedUIUrl } from '../config/cognito';
import analytics from '../lib/tracking';
import { startLoginJourney } from '../lib/telemetry';
import {
  refreshIdToken,
  RefreshError,
  isTokenExpiringSoon,
} from '../lib/auth-tokens';

// Refresh the id token when it's within this many seconds of expiry, so a
// request doesn't race the clock and 401. Cognito access/id tokens default to
// a 1h lifetime; 120s of skew is comfortably inside that.
const TOKEN_EXPIRY_SKEW_SECONDS = 120;

// Type definitions
export interface User {
  email: string;
  name?: string;
  picture?: string;
  // Cognito subject claim — stable, non-PII identifier we pass to GA4 as
  // user_id. Optional because users restored from localStorage written
  // before this field was tracked may not have it.
  sub?: string;
  [key: string]: unknown;
}

export interface AuthTokens {
  accessToken: string;
  refreshToken?: string;
  idToken?: string;
}

export interface UTMParams {
  utm_source?: string | null;
  utm_medium?: string | null;
  utm_campaign?: string | null;
  utm_content?: string | null;
  utm_term?: string | null;
}

export interface AuthContextValue {
  user: User | null;
  loading: boolean;
  isAuthenticated: boolean;
  signInWithGoogle: () => void;
  handleGoogleCallback: (code: string, utmParams?: UTMParams) => Promise<void>;
  logout: () => void;
  getAuthHeaders: () => Record<string, string>;
  /**
   * Exchange the stored refresh token for a fresh id token and persist it.
   * Resolves true if a new token was stored, false if there was nothing to
   * refresh or the refresh failed (in which case auth is cleared and the user
   * is dropped to anonymous with a toast). Callers use this to recover from a
   * 401 and retry the original request once. Concurrent calls share a single
   * in-flight refresh.
   */
  refreshTokens: () => Promise<boolean>;
  /**
   * Like getAuthHeaders, but proactively refreshes first if the stored id
   * token is expired or about to expire. Prefer this for request paths so we
   * refresh ahead of a 401 rather than reacting to one.
   */
  getValidAuthHeaders: () => Promise<Record<string, string>>;
}

// Create context with undefined initial value
export const AuthContext = createContext<AuthContextValue | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Bump this to force every currently-logged-in browser to wipe its stored
 * tokens and go back through the sign-in flow on next page load. Used when
 * something about the auth shape changes (token format, claim semantics)
 * and we don't want to keep speculating about whose localStorage is stale.
 *
 * The previous values are dead history — only the current value matters.
 * Cognito sessions are NOT invalidated by this; users can re-login with
 * one click through the hosted UI. To kill server-side sessions too, use
 * Cognito GlobalSignOut per user.
 */
const AUTH_WIPE_VERSION = 'v1';
const AUTH_WIPE_VERSION_KEY = 'auth_wipe_version';

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Check for existing auth on mount
  useEffect(() => {
    // One-shot wipe: anyone whose localStorage is from before this version
    // of the app is logged out silently.
    const wipeVersion = localStorage.getItem(AUTH_WIPE_VERSION_KEY);
    if (wipeVersion !== AUTH_WIPE_VERSION) {
      localStorage.removeItem('user');
      localStorage.removeItem('auth_tokens');
      localStorage.setItem(AUTH_WIPE_VERSION_KEY, AUTH_WIPE_VERSION);
      setLoading(false);
      return;
    }

    const storedUser = localStorage.getItem('user');
    const tokens = getStoredTokens();

    if (storedUser && tokens?.idToken) {
      try {
        const parsed = JSON.parse(storedUser) as User;
        setUser(parsed);
        if (parsed.sub) {
          analytics.identify(parsed.sub, { user_type: 'authenticated' });
        }
      } catch (error) {
        console.error('Invalid stored user data:', error);
        Sentry.captureException(error, {
          level: 'warning',
          tags: { error_type: 'invalid_stored_user' }
        });
        clearAuth();
      }
    }
    setLoading(false);
  }, []);

  const getStoredTokens = (): AuthTokens | null => {
    try {
      const tokens = localStorage.getItem('auth_tokens');
      return tokens ? JSON.parse(tokens) : null;
    } catch {
      return null;
    }
  };

  const storeTokens = (tokens: AuthTokens): void => {
    localStorage.setItem('auth_tokens', JSON.stringify(tokens));
  };

  const clearAuth = (): void => {
    localStorage.removeItem('user');
    localStorage.removeItem('auth_tokens');
    setUser(null);
  };

  /**
   * Redirect to Cognito hosted UI for Google sign-in
   */
  const signInWithGoogle = (): void => {
    // Open the login journey span. It can't live in memory because we're
    // about to leave the page entirely — the start timestamp is stashed
    // in sessionStorage; GoogleCallbackPage creates the backdated span
    // once we return.
    startLoginJourney();

    const hostedUIUrl = getCognitoHostedUIUrl();
    const redirectUri = `${window.location.origin}/auth/google/callback`;

    // Build OAuth authorize URL
    const params = new URLSearchParams({
      client_id: cognitoConfig.clientId,
      response_type: 'code',
      scope: 'email openid profile',
      redirect_uri: redirectUri,
      identity_provider: 'Google',
    });

    const authorizeUrl = `${hostedUIUrl}/oauth2/authorize?${params.toString()}`;

    // Redirect to Cognito hosted UI
    window.location.href = authorizeUrl;
  };

  /**
   * Handle OAuth callback - exchange authorization code for tokens
   */
  const handleGoogleCallback = async (code: string, utmParams?: UTMParams): Promise<void> => {
    try {
      const url = new URL('auth/google/callback', API_BASE_URL);
      const requestBody: { code: string; utm_params?: UTMParams } = { code };

      // Include UTM params if provided
      if (utmParams) {
        requestBody.utm_params = utmParams;
      }

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const data = await response.json();
        const errorMessage = data.error || 'Failed to authenticate with Google';
        const error = new Error(errorMessage);

        // Log full error details to console for debugging
        console.error('OAuth callback failed:', {
          status: response.status,
          statusText: response.statusText,
          error: data.error,
          details: data.details,
          redirect_uri: data.redirect_uri,
        });

        // Capture full error context in Sentry
        Sentry.captureException(error, {
          level: 'error',
          tags: {
            error_type: 'google_oauth_callback_http_error',
            http_status: response.status.toString(),
          },
          extra: {
            response_status: response.status,
            response_status_text: response.statusText,
            response_body: data,
            request_code: code.substring(0, 20) + '...', // Truncate for privacy
            request_has_utm: !!utmParams,
          },
        });

        throw error;
      }

      const rawData = await response.json();

      // Validate the response shape against the shared schema. If the
      // backend produces something off-spec (snake_case vs camelCase,
      // missing field, wrong type) we fail loudly here instead of silently
      // storing garbage and breaking auth downstream.
      const data = GoogleCallbackResponseSchema.parse(rawData);

      const normalizedTokens: AuthTokens = {
        accessToken: data.tokens.access_token,
        idToken: data.tokens.id_token,
        refreshToken: data.tokens.refresh_token,
      };
      storeTokens(normalizedTokens);
      const userData: User = {
        email: data.user.email,
        name: data.user.name,
        picture: data.user.picture,
        sub: data.user.sub,
      };
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
      analytics.identify(data.user.sub, { user_type: 'authenticated' });
    } catch (error) {
      console.error('Google OAuth callback error:', error);

      // Only send to Sentry if this is a network error (not HTTP error)
      // HTTP errors are already captured above with full context
      if (error instanceof TypeError && error.message.includes('fetch')) {
        Sentry.captureException(error, {
          level: 'error',
          tags: { error_type: 'google_oauth_callback_network_error' },
          extra: {
            has_code: !!code,
            has_utm: !!utmParams,
            error_message: error.message,
          },
        });
      }

      throw error;
    }
  };

  const logout = (): void => {
    clearAuth();
  };

  /**
   * Refresh the id token using the stored refresh token. On success the new
   * tokens are persisted and we return true. On failure (refresh token
   * expired/revoked, or no token to refresh) we clear auth, drop to anonymous,
   * surface a toast, and return false — never leave the app wedged on a stale
   * token showing "couldn't load".
   */
  // Holds the in-flight refresh so concurrent callers (e.g. a catalog fetch
  // and an audio fetch both hitting 401 at once) share ONE network refresh
  // instead of each firing their own and racing to overwrite tokens.
  const refreshInFlight = useRef<Promise<boolean> | null>(null);

  const doRefresh = async (): Promise<boolean> => {
    const tokens = getStoredTokens();
    if (!tokens?.refreshToken) {
      return false;
    }

    try {
      const refreshed = await refreshIdToken(tokens.refreshToken, API_BASE_URL);
      // Re-read: another flow may have updated tokens while we awaited.
      const latest = getStoredTokens() ?? tokens;
      storeTokens({
        ...latest,
        accessToken: refreshed.accessToken,
        idToken: refreshed.idToken,
      });
      return true;
    } catch (error) {
      // A failed refresh means the session is genuinely dead. Drop to
      // anonymous rather than loop on 401s.
      console.warn('Token refresh failed; signing out:', error);
      if (!(error instanceof RefreshError)) {
        Sentry.captureException(error, {
          level: 'warning',
          tags: { error_type: 'token_refresh_failed' },
        });
      }
      clearAuth();
      toast.error('Your session expired. Please sign in again.');
      return false;
    }
  };

  const refreshTokens = (): Promise<boolean> => {
    // Single-flight: if a refresh is already running, return the same promise.
    if (refreshInFlight.current) {
      return refreshInFlight.current;
    }
    const p = doRefresh().finally(() => {
      refreshInFlight.current = null;
    });
    refreshInFlight.current = p;
    return p;
  };

  const getAuthHeaders = (): Record<string, string> => {
    const tokens = getStoredTokens();
    return tokens?.idToken
      ? { Authorization: `Bearer ${tokens.idToken}` }
      : {};
  };

  const getValidAuthHeaders = async (): Promise<Record<string, string>> => {
    const tokens = getStoredTokens();
    if (!tokens?.idToken) return {};

    // Proactively refresh if the id token is expired or about to expire.
    // refreshTokens() is single-flight and, on failure, clears auth — in which
    // case we fall through to anonymous (no Authorization header).
    if (isTokenExpiringSoon(tokens.idToken, TOKEN_EXPIRY_SKEW_SECONDS)) {
      const ok = await refreshTokens();
      if (!ok) return {};
    }
    return getAuthHeaders();
  };

  // When a tab regains focus after being backgrounded, its id token may have
  // expired while idle. Proactively refresh so the next request doesn't 401.
  // refreshTokens is single-flight and self-clearing on failure.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      const tokens = getStoredTokens();
      if (
        tokens?.idToken &&
        isTokenExpiringSoon(tokens.idToken, TOKEN_EXPIRY_SKEW_SECONDS)
      ) {
        void refreshTokens();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
    // refreshTokens/getStoredTokens are stable for the provider's lifetime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Compute isAuthenticated directly from state
  const isAuthenticated = !!user && !!getStoredTokens()?.idToken;

  const value: AuthContextValue = {
    user,
    loading,
    isAuthenticated,
    signInWithGoogle,
    handleGoogleCallback,
    logout,
    getAuthHeaders,
    refreshTokens,
    getValidAuthHeaders,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
